// May 28, 2026 — Universal Zero Balance Surface Guard hook.
//
// Returns the guard state for a given LLM surface (workbench_run,
// refinery_run, public_ask, ai_assistant, etc.) so any tile/button that
// would trigger an LLM call can grey itself out + show a popup on
// click instead of letting the user blindly fire a call that the
// backend will just 402-block.
//
// Excludes Fan Portal — those calls spend the silo OWNER's funds, not
// the visitor's, so the visitor never gets greyed.
import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";

export function useLLMGuardState(surface, siloId = null, { enabled = true, provider = null } = {}) {
  const [state, setState] = useState({
    loading: enabled,
    blocked: false,
    provider: null,
    byok: false,
    bucketCents: 0,
    consoleUrl: "#",
  });
  const lastKey = useRef(null);

  useEffect(() => {
    if (!enabled || !surface) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    // Jun 2026 — `provider` forces a lane (the voice pill is ALWAYS OpenAI,
    // regardless of the silo's text-answer provider).
    const key = `${surface}|${siloId || ""}|${provider || ""}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    setState((s) => ({ ...s, loading: true }));
    const params = new URLSearchParams({ surface });
    if (siloId) params.set("silo_id", siloId);
    if (provider) params.set("provider", provider);
    api.get(`/me/llm-guard-state?${params.toString()}`)
      .then((r) => {
        const d = r.data || {};
        setState({
          loading: false,
          blocked: !!d.blocked,
          provider: d.provider || null,
          byok: !!d.byok,
          bucketCents: Number(d.bucket_cents || 0),
          consoleUrl: d.console_url || "#",
        });
      })
      .catch(() => {
        // Network blip — fail open. The backend guard still blocks the
        // actual LLM call, so the user is never charged. We just don't
        // pre-grey the surface.
        setState({
          loading: false,
          blocked: false,
          provider: null,
          byok: false,
          bucketCents: 0,
          consoleUrl: "#",
        });
      });
  }, [surface, siloId, enabled, provider]);

  return state;
}

// Helper: emit the same window event the LLMGuardModal already listens
// for, so a click on a greyed surface pops the existing teach-modal
// without any new wiring.
export function triggerLLMGuardModal({ provider, surface, consoleUrl }) {
  try {
    window.dispatchEvent(new CustomEvent("llm-guard-blocked", {
      detail: {
        provider: provider || "gemini",
        surface: surface || "unknown",
        bucket_cents: 0,
        console_url: consoleUrl || "#",
      },
    }));
  } catch { /* swallow */ }
}
